import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import GroupIcon from "@mui/icons-material/Group";
import PercentIcon from "@mui/icons-material/Percent";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import { Box, Grid, Paper, Stack, Typography } from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useMemo } from "react";
import { useFragment } from "react-relay";
import { InternBrandStatCards_brand$key } from "./__generated__/InternBrandStatCards_brand.graphql";
import { useColorMode } from "../../../contexts/ColorModeContext";
import formatNumber from "../../../utils/formatNumber";

const brandFragment = graphql`
  fragment InternBrandStatCards_brand on BrandType {
    metricsDaily(filters: { os: { isNull: true } }, first: 30) {
      edges {
        node {
          date
          appSales
          appSessions
          newAppUsers
        }
      }
    }
    orderMetricsSummary {
      totalOrders
      kinnOrders
      attributionRate
    }
  }
`;

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
}

const StatCard = ({ title, value, subtitle, icon }: StatCardProps) => {
  const { colorMode } = useColorMode();

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        bgcolor: "var(--intern-bg-primary)",
        border: "1px solid var(--intern-border-default)",
        borderRadius: 2,
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box
          sx={{
            p: 1,
            borderRadius: 1.5,
            bgcolor:
              colorMode === "dark"
                ? "rgba(99, 102, 241, 0.15)"
                : "rgba(99, 102, 241, 0.1)",
            color: "#6366f1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: "var(--intern-text-secondary)",
              fontSize: "0.75rem",
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              mb: 0.5,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: "var(--intern-text-primary)",
              fontWeight: 700,
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              lineHeight: 1.2,
            }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-tertiary)",
                fontSize: "0.6875rem",
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};

type Props = {
  brand: InternBrandStatCards_brand$key;
};

const InternBrandStatCards = ({ brand: brandKey }: Props) => {
  const brand = useFragment(brandFragment, brandKey);

  // Aggregate 30-day metrics
  const metrics30d = useMemo(() => {
    const edges = brand.metricsDaily?.edges ?? [];
    let totalSales = 0;
    let totalSessions = 0;
    let totalNewUsers = 0;

    edges.forEach((edge) => {
      if (edge?.node) {
        totalSales += edge.node.appSales ?? 0;
        totalSessions += edge.node.appSessions ?? 0;
        totalNewUsers += edge.node.newAppUsers ?? 0;
      }
    });

    return {
      sales: totalSales,
      sessions: totalSessions,
      newUsers: totalNewUsers,
    };
  }, [brand.metricsDaily]);

  const orderMetrics = brand.orderMetricsSummary;
  const attributionRate = orderMetrics?.attributionRate ?? 0;

  return (
    <Grid container spacing={2}>
      <Grid item xs={6} md={3}>
        <StatCard
          title="App Users (30d)"
          value={formatNumber(metrics30d.newUsers)}
          subtitle="New users acquired"
          icon={<GroupIcon />}
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <StatCard
          title="App Sales (30d)"
          value={formatNumber(metrics30d.sales, "currency")}
          subtitle="Total app revenue"
          icon={<AttachMoneyIcon />}
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <StatCard
          title="Sessions (30d)"
          value={formatNumber(metrics30d.sessions)}
          subtitle="Total app sessions"
          icon={<TouchAppIcon />}
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <StatCard
          title="Attribution (7d)"
          value={`${attributionRate.toFixed(1)}%`}
          subtitle={`${orderMetrics?.kinnOrders ?? 0}/${
            orderMetrics?.totalOrders ?? 0
          } orders`}
          icon={<PercentIcon />}
        />
      </Grid>
    </Grid>
  );
};

export default InternBrandStatCards;
